import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Truck, ShieldCheck, Wrench, RotateCcw, Star, Check } from "lucide-react";
import { api, inr } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";
import CustomizationDialog from "@/components/CustomizationDialog";
import { toast } from "sonner";

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, toggleWishlist, inWishlist } = useShop();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [pin, setPin] = useState("");
  const [pinRes, setPinRes] = useState(null);
  const [showCustom, setShowCustom] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => { setP(r.data); setImg(0); }).catch(() => setP(null));
  }, [slug]);

  if (!p) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  const discount = p.mrp && p.mrp > p.price ? Math.round((1 - p.price / p.mrp) * 100) : 0;
  const isCustom = p.customizable || p.product_type === "vendor";
  const isFurniture = p.mart === "wood-mart";
  const wished = inWishlist(p.id);

  const checkPin = async () => {
    const { data } = await api.post("/pincode-check", { pincode: pin });
    setPinRes(data);
  };
  const buyNow = async () => {
    const ok = await addToCart(p.id);
    if (ok) navigate("/cart");
  };
  const submitReview = async () => {
    if (!user?.user_id) { toast.error("Please sign in to review"); return; }
    if (!comment.trim()) return;
    await api.post("/reviews", { product_id: p.id, rating, comment });
    toast.success("Review added");
    setComment("");
    const { data } = await api.get(`/products/${slug}`);
    setP(data);
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-8">
      <nav className="mb-6 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-midnight">Home</Link> / <Link to={`/${p.mart}`} className="hover:text-midnight capitalize">{p.mart.replace("-", " ")}</Link> / <span className="text-midnight">{p.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div className="flex gap-4">
          <div className="flex flex-col gap-3">
            {p.images.map((im, i) => (
              <button key={i} onClick={() => setImg(i)} data-testid={`thumb-${i}`} className={`h-16 w-16 overflow-hidden rounded-xl border-2 ${img === i ? "border-gold" : "border-transparent"}`}>
                <img src={im} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-hidden rounded-3xl border border-border bg-secondary group">
            <img src={p.images[img]} alt={p.name} className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-110" />
          </div>
        </div>

        {/* Info */}
        <div>
          <span className="text-sm font-bold uppercase tracking-wide text-gold-dark">{p.brand}</span>
          <h1 className="mt-1 text-3xl font-extrabold text-midnight">{p.name}</h1>
          {p.product_type === "signature" && <span className="mt-2 inline-block rounded-full bg-midnight px-3 py-1 text-xs font-bold text-gold">Wood Mart Signature Collection</span>}
          {p.product_type === "vendor" && p.vendor_id && <Link to={`/vendors/${p.vendor_id}`} className="mt-2 inline-block text-sm font-semibold text-mart-wood underline">Vendor Collection →</Link>}

          <div className="mt-4 flex items-end gap-3">
            <span className="text-3xl font-extrabold text-midnight">{inr(p.price)}</span>
            {p.mrp && p.mrp > p.price && <span className="text-lg text-muted-foreground line-through">{inr(p.mrp)}</span>}
            {discount > 0 && <span className="rounded-full bg-gold px-2.5 py-1 text-xs font-bold text-midnight">{discount}% OFF</span>}
          </div>
          <p className="text-xs text-muted-foreground">Inclusive of all taxes</p>

          <p className="mt-5 text-sm text-muted-foreground leading-relaxed">{p.description}</p>

          {/* CTAs */}
          <div className="mt-6 flex flex-wrap gap-3">
            {isCustom ? (
              <button data-testid="request-customization-btn" onClick={() => setShowCustom(true)} className="rounded-full bg-mart-wood px-8 py-3.5 text-sm font-bold text-white hover:opacity-90 transition-opacity">Request Customization</button>
            ) : (
              <>
                <button data-testid="pdp-add-to-cart" onClick={() => addToCart(p.id)} className="flex items-center gap-2 rounded-full bg-midnight px-8 py-3.5 text-sm font-bold text-white hover:bg-midnight/90 transition-colors"><ShoppingBag className="h-4 w-4" /> Add to Cart</button>
                <button data-testid="pdp-buy-now" onClick={buyNow} className="rounded-full bg-gold px-8 py-3.5 text-sm font-bold text-midnight hover:bg-gold-soft transition-colors">Buy Now</button>
              </>
            )}
            <button onClick={() => toggleWishlist(p)} data-testid="pdp-wishlist" className="grid h-12 w-12 place-items-center rounded-full border border-border hover:bg-secondary transition-colors">
              <Heart className={`h-5 w-5 ${wished ? "fill-red-500 text-red-500" : ""}`} />
            </button>
          </div>

          {/* Pincode */}
          <div className="mt-6 rounded-2xl border border-border p-4">
            <p className="text-sm font-bold text-midnight">Check delivery & installation</p>
            <div className="mt-2 flex gap-2">
              <input data-testid="pincode-input" value={pin} onChange={(e) => setPin(e.target.value)} maxLength={6} placeholder="Enter 6-digit pincode" className="flex-1 rounded-full border border-border px-4 py-2.5 text-sm outline-none focus:border-gold" />
              <button data-testid="pincode-check-btn" onClick={checkPin} className="rounded-full bg-midnight px-5 py-2.5 text-sm font-semibold text-white">Check</button>
            </div>
            {pinRes && (
              <div className={`mt-3 text-sm ${pinRes.serviceable ? "text-mart-home" : "text-red-600"}`} data-testid="pincode-result">
                {pinRes.serviceable ? (
                  <span className="flex items-center gap-1.5"><Check className="h-4 w-4" /> Delivery in {pinRes.estimated_days} days • {pinRes.delivery_charge === 0 ? "Free delivery" : inr(pinRes.delivery_charge)}{pinRes.installation_available && isFurniture ? " • Installation available" : ""}</span>
                ) : pinRes.message}
              </div>
            )}
          </div>

          {/* Trust badges */}
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[[Truck, "Delivery", p.delivery_info?.split("—")[0] || "2-4 days"],
              ...(isFurniture ? [[Wrench, "Installation", p.installation || "On request"]] : []),
              [ShieldCheck, "Warranty", p.warranty || "As per brand"],
              [RotateCcw, "Returns", "Easy returns"]].map(([Icon, t, s], i) => (
              <div key={i} className="rounded-2xl border border-border p-3">
                <Icon className="h-5 w-5 text-gold-dark" />
                <p className="mt-1.5 text-xs font-bold text-midnight">{t}</p>
                <p className="text-[11px] text-muted-foreground line-clamp-1">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Specs & furniture details */}
      {(Object.keys(p.specs || {}).length > 0 || isFurniture) && (
        <Reveal className="mt-14">
          <h2 className="text-2xl font-extrabold text-midnight mb-4">Specifications</h2>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-2 rounded-3xl border border-border p-6">
            {Object.entries(p.specs || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-border py-2 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-semibold text-midnight">{v}</span></div>
            ))}
            {p.material && <div className="flex justify-between border-b border-border py-2 text-sm"><span className="text-muted-foreground">Material</span><span className="font-semibold text-midnight">{p.material}</span></div>}
            {p.finish && <div className="flex justify-between border-b border-border py-2 text-sm"><span className="text-muted-foreground">Finish</span><span className="font-semibold text-midnight">{p.finish}</span></div>}
            {p.dimensions && <div className="flex justify-between border-b border-border py-2 text-sm"><span className="text-muted-foreground">Dimensions</span><span className="font-semibold text-midnight">{p.dimensions}</span></div>}
          </div>
        </Reveal>
      )}

      {/* Reviews */}
      <Reveal className="mt-14">
        <h2 className="text-2xl font-extrabold text-midnight mb-4">Ratings & Reviews</h2>
        {p.reviews.length === 0 && <p className="text-sm text-muted-foreground mb-4">No reviews yet. Be the first to review this product.</p>}
        <div className="space-y-4 mb-6">
          {p.reviews.map((r) => (
            <div key={r.id} className="rounded-2xl border border-border p-4">
              <div className="flex items-center gap-2">
                <div className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < r.rating ? "fill-gold text-gold" : "text-border"}`} />)}</div>
                <span className="text-sm font-bold text-midnight">{r.name}</span>
              </div>
              <p className="mt-1.5 text-sm text-muted-foreground">{r.comment}</p>
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-border p-5 max-w-xl">
          <p className="text-sm font-bold text-midnight mb-2">Write a review</p>
          <div className="flex gap-1 mb-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <button key={i} onClick={() => setRating(i + 1)} data-testid={`review-star-${i + 1}`}><Star className={`h-6 w-6 ${i < rating ? "fill-gold text-gold" : "text-border"}`} /></button>
            ))}
          </div>
          <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience…" className="w-full rounded-xl border border-border p-3 text-sm outline-none focus:border-gold" rows={3} />
          <button data-testid="submit-review-btn" onClick={submitReview} className="mt-3 rounded-full bg-midnight px-6 py-2.5 text-sm font-semibold text-white">Submit Review</button>
        </div>
      </Reveal>

      {/* Frequently bought / related */}
      {p.related?.length > 0 && (
        <Reveal className="mt-14">
          <h2 className="text-2xl font-extrabold text-midnight mb-6">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {p.related.map((rp) => <ProductCard key={rp.id} product={rp} />)}
          </div>
        </Reveal>
      )}

      {showCustom && <CustomizationDialog product={p} onClose={() => setShowCustom(false)} />}
    </div>
  );
}
